import type { GlanceDir } from '../sim/types';
import type { InputAction, InputSource, SourceSample } from './InputSource';

/**
 * Teclado con pedales "virtuales": las teclas rampan el pedal de forma
 * progresiva para que sea posible conducir con suavidad sin analógicos.
 *
 * Mapa unificado (automático y manual):
 *   W/↑ acelerar · S/↓ frenar · A/D ←/→ dirección
 *   Shift PISAR embrague · Ctrl SOLTAR embrague (graduado)
 *   1..6 marchas (manual) · R reversa · N punto muerto · P/D (automático)
 *   J/K marcha abajo/arriba · F arrancar el motor (mantener)
 *   Z/X/C espejos izq./interior/dcho. · V cámara
 *   Q/E intermitentes · Espacio freno de mano · L luces · B cinturón · H claxon
 */
export class KeyboardSource implements InputSource {
  private held = new Set<string>();
  private actions: InputAction[] = [];
  private throttle = 0;
  private brake = 0;
  private steer = 0;
  private glanceStack: GlanceDir[] = [];

  private onKeyDown = (e: KeyboardEvent): void => {
    if (e.repeat) return;
    const k = e.key.toLowerCase();
    const code = e.code;
    if (
      ['arrowup', 'arrowdown', 'arrowleft', 'arrowright', ' '].includes(k) ||
      code === 'Space'
    ) {
      e.preventDefault();
    }
    this.held.add(code);
    switch (code) {
      case 'KeyP':
        this.actions.push({ type: 'gear', gear: 'P' });
        break;
      case 'KeyR':
        this.actions.push({ type: 'gear', gear: 'R' });
        break;
      case 'KeyN':
        this.actions.push({ type: 'gear', gear: 'N' });
        break;
      case 'KeyD':
        this.actions.push({ type: 'gear', gear: 'D' });
        break;
      case 'Digit1':
        this.actions.push({ type: 'gear', gear: 1 });
        break;
      case 'Digit2':
        this.actions.push({ type: 'gear', gear: 2 });
        break;
      case 'Digit3':
        this.actions.push({ type: 'gear', gear: 3 });
        break;
      case 'Digit4':
        this.actions.push({ type: 'gear', gear: 4 });
        break;
      case 'Digit5':
        this.actions.push({ type: 'gear', gear: 5 });
        break;
      case 'Digit6':
        this.actions.push({ type: 'gear', gear: 6 });
        break;
      case 'KeyJ':
        this.actions.push({ type: 'gearCycle', dir: -1 });
        break;
      case 'KeyK':
        this.actions.push({ type: 'gearCycle', dir: 1 });
        break;
      case 'KeyF':
        this.actions.push({ type: 'ignitionDown', down: true });
        break;
      case 'KeyQ':
        this.actions.push({ type: 'signal', side: 'left' });
        break;
      case 'KeyE':
        this.actions.push({ type: 'signal', side: 'right' });
        break;
      case 'KeyL':
        this.actions.push({ type: 'lights' });
        break;
      case 'KeyB':
        this.actions.push({ type: 'belt' });
        break;
      case 'KeyV':
        this.actions.push({ type: 'camera' });
        break;
      case 'KeyH':
        this.actions.push({ type: 'hornDown', down: true });
        break;
      case 'Space':
        this.actions.push({ type: 'handbrake' });
        break;
      case 'Escape':
        this.actions.push({ type: 'pause' });
        break;
      case 'KeyZ':
        this.pushGlance('left');
        break;
      case 'KeyX':
        this.pushGlance('rear');
        break;
      case 'KeyC':
        this.pushGlance('right');
        break;
    }
  };

  private onKeyUp = (e: KeyboardEvent): void => {
    this.held.delete(e.code);
    switch (e.code) {
      case 'KeyH':
        this.actions.push({ type: 'hornDown', down: false });
        break;
      case 'KeyF':
        this.actions.push({ type: 'ignitionDown', down: false });
        break;
      case 'KeyZ':
        this.popGlance('left');
        break;
      case 'KeyX':
        this.popGlance('rear');
        break;
      case 'KeyC':
        this.popGlance('right');
        break;
    }
  };

  private onBlur = (): void => {
    if (this.held.has('KeyH')) this.actions.push({ type: 'hornDown', down: false });
    if (this.held.has('KeyF')) this.actions.push({ type: 'ignitionDown', down: false });
    this.held.clear();
    this.glanceStack = [];
  };

  constructor(private target: Window = window) {
    target.addEventListener('keydown', this.onKeyDown);
    target.addEventListener('keyup', this.onKeyUp);
    target.addEventListener('blur', this.onBlur);
  }

  private pushGlance(dir: GlanceDir): void {
    this.glanceStack = this.glanceStack.filter((g) => g !== dir);
    this.glanceStack.push(dir);
  }
  private popGlance(dir: GlanceDir): void {
    this.glanceStack = this.glanceStack.filter((g) => g !== dir);
  }

  sample(dt: number): SourceSample {
    const up = this.held.has('KeyW') || this.held.has('ArrowUp');
    const down = this.held.has('KeyS') || this.held.has('ArrowDown');
    const left = this.held.has('KeyA') || this.held.has('ArrowLeft');
    const right = this.held.has('KeyD') || this.held.has('ArrowRight');

    // Pedales progresivos: subida más lenta que la bajada
    this.throttle = up
      ? Math.min(1, this.throttle + dt * 1.6)
      : Math.max(0, this.throttle - dt * 3.5);
    this.brake = down ? Math.min(1, this.brake + dt * 2.6) : Math.max(0, this.brake - dt * 5);

    const steerTarget = (right ? 1 : 0) - (left ? 1 : 0);
    if (steerTarget !== 0) {
      this.steer += Math.sign(steerTarget - this.steer) * Math.min(dt * 2.6, Math.abs(steerTarget - this.steer));
    } else {
      // autocentrado
      const back = Math.min(dt * 3.2, Math.abs(this.steer));
      this.steer -= Math.sign(this.steer) * back;
    }

    return {
      throttle: this.throttle,
      brake: this.brake,
      steer: this.steer,
      glance: this.glanceStack[this.glanceStack.length - 1] ?? 'none',
      clutchAnalog: 0,
      clutchHold: this.held.has('ShiftLeft') || this.held.has('ShiftRight'),
      clutchFeed: this.held.has('ControlLeft') || this.held.has('ControlRight'),
      clutchMomentary: false,
    };
  }

  drainActions(): InputAction[] {
    const out = this.actions;
    this.actions = [];
    return out;
  }

  dispose(): void {
    this.target.removeEventListener('keydown', this.onKeyDown);
    this.target.removeEventListener('keyup', this.onKeyUp);
    this.target.removeEventListener('blur', this.onBlur);
  }
}
